import { type UserDistrictSummary, type UserDto } from "@crowdcoursing/api/openapi";

import { userDistrictSummaryDisplayName } from "~ui/pages/AdminPage/schoolUserManagementHooks";

export function sortFacultyByName(a: UserDistrictSummary, b: UserDistrictSummary): number {
  const last = (a.lastName ?? "").localeCompare(b.lastName ?? "", undefined, { sensitivity: "base" });
  if (last !== 0) {
    return last;
  }
  return (a.firstName ?? "").localeCompare(b.firstName ?? "", undefined, { sensitivity: "base" });
}

export function facultyInitials(user: UserDistrictSummary): string {
  const f = user.firstName?.trim()?.[0] ?? "";
  const l = user.lastName?.trim()?.[0] ?? "";
  const s = `${f}${l}`.toUpperCase();
  return s || "?";
}

/** Last-name-first display; use `whenEmpty` when both names are missing (e.g. `"Unknown"`, `"—"`). */
export function lastFirstDisplayNameFromParts(
  person: { firstName?: string | null; lastName?: string | null },
  whenEmpty: string,
): string {
  const last = person.lastName?.trim() ?? "";
  const first = person.firstName?.trim() ?? "";
  if (last && first) {
    return `${last}, ${first}`;
  }
  if (last) {
    return last;
  }
  if (first) {
    return first;
  }
  return whenEmpty;
}

export function studentDisplayNameLastFirst(user: UserDistrictSummary): string {
  return lastFirstDisplayNameFromParts(user, "Unknown");
}

export function sortStudentsByName(a: UserDistrictSummary, b: UserDistrictSummary): number {
  const last = (a.lastName ?? "").localeCompare(b.lastName ?? "", undefined, { sensitivity: "base" });
  if (last !== 0) {
    return last;
  }
  return (a.firstName ?? "").localeCompare(b.firstName ?? "", undefined, { sensitivity: "base" });
}

export function studentMatchesQuery(user: UserDistrictSummary, q: string): boolean {
  if (!q.trim()) {
    return true;
  }
  const needle = q.trim().toLowerCase();
  const name = studentDisplayNameLastFirst(user).toLowerCase();
  const email = (user.email ?? "").toLowerCase();
  return name.includes(needle) || email.includes(needle);
}

export function studentInitials(user: UserDistrictSummary): string {
  const f = user.firstName?.trim()?.[0] ?? "";
  const l = user.lastName?.trim()?.[0] ?? "";
  const s = `${f}${l}`.toUpperCase();
  return s || "?";
}

export function parentUserDtoDisplayNameLastFirst(user: UserDto): string {
  const lastFirst = lastFirstDisplayNameFromParts({ firstName: user.firstName, lastName: user.lastName }, "");
  if (lastFirst) {
    return lastFirst;
  }
  return user.email?.trim() || "Parent";
}

export function facultyMatchesQuery(user: UserDistrictSummary, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return false;
  }
  const name = userDistrictSummaryDisplayName(user).toLowerCase();
  const email = (user.email ?? "").toLowerCase();
  return name.includes(needle) || email.includes(needle);
}
